import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Phone, PhoneCall, PhoneOff, PhoneMissed, Clock, User, Star,
  ChevronRight, Zap, Target, RefreshCw, FileText, ArrowRight,
  CheckCircle, XCircle, CalendarClock, AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { stripMarkdownObj } from "../utils/stripMarkdown";
import { CopyButton } from "../components/CopyButton";

const formatINR = (v) => {
  if (!v) return "N/A";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const SCORE_COLORS = {
  hot: "bg-red-100 text-red-700", warm: "bg-amber-100 text-amber-700",
  nurture: "bg-blue-100 text-blue-700", new: "bg-emerald-100 text-emerald-700",
  dormant: "bg-slate-100 text-slate-600",
};

const OUTCOMES = [
  { value: "connected", label: "Connected", icon: PhoneCall, color: "text-emerald-600" },
  { value: "no_answer", label: "No Answer", icon: PhoneMissed, color: "text-amber-600" },
  { value: "callback", label: "Callback", icon: CalendarClock, color: "text-blue-600" },
  { value: "not_interested", label: "Not Interested", icon: PhoneOff, color: "text-red-600" },
  { value: "wrong_number", label: "Wrong Number", icon: XCircle, color: "text-slate-500" },
];

const TelecallerQueuePage = () => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [briefOpen, setBriefOpen] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({ outcome: "", notes: "", callback_time: "", next_stage: "" });

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/telecaller/queue?limit=30`);
      setQueue(res.data);
    } catch { toast.error("Failed to load queue"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQueue(); }, []);

  const fetchBrief = async (leadId) => {
    setBriefLoading(true);
    setBriefOpen(true);
    setBrief(null);
    try {
      const res = await axios.get(`${API}/telecaller/brief/${leadId}`);
      setBrief(stripMarkdownObj(res.data));
    } catch { toast.error("Failed to generate brief"); setBriefOpen(false); }
    finally { setBriefLoading(false); }
  };

  const openOutcome = (lead) => {
    setSelectedLead(lead);
    setOutcomeForm({ outcome: "", notes: "", callback_time: "", next_stage: "" });
    setOutcomeOpen(true);
  };

  const submitOutcome = async () => {
    if (!outcomeForm.outcome) { toast.error("Select an outcome"); return; }
    try {
      await axios.post(`${API}/telecaller/outcome/${selectedLead.lead_id}`, outcomeForm);
      toast.success("Outcome logged");
      setOutcomeOpen(false);
      fetchQueue();
    } catch { toast.error("Failed to log outcome"); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="telecaller-queue-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Telecaller Queue</h1>
            <p className="text-sm text-slate-500 mt-1">AI-prioritized leads to call — with pre-call briefs</p>
          </div>
          <Button variant="outline" onClick={fetchQueue} disabled={loading} data-testid="refresh-queue-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Phone className="w-5 h-5 text-blue-700" /></div>
            <div><p className="text-xs text-slate-500">In Queue</p><p className="text-lg font-bold text-slate-900">{queue.length}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><Star className="w-5 h-5 text-red-700" /></div>
            <div><p className="text-xs text-slate-500">Hot Leads</p><p className="text-lg font-bold text-red-600">{queue.filter(q => q.score === "hot").length}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Target className="w-5 h-5 text-emerald-700" /></div>
            <div><p className="text-xs text-slate-500">New Leads</p><p className="text-lg font-bold text-emerald-600">{queue.filter(q => q.stage === "new").length}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><CalendarClock className="w-5 h-5 text-amber-700" /></div>
            <div><p className="text-xs text-slate-500">Callbacks</p><p className="text-lg font-bold text-amber-600">{queue.filter(q => q.has_callback).length}</p></div>
          </CardContent></Card>
        </div>

        {/* Queue */}
        {loading ? (
          <div className="py-12 text-center text-slate-400"><RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin opacity-50" /><p>Loading queue...</p></div>
        ) : queue.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-slate-400">
            <Phone className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No leads in the queue right now</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {queue.map((lead, idx) => (
              <Card key={lead.lead_id} className="hover:shadow-md transition-shadow" data-testid={`queue-card-${lead.lead_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Priority rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      idx < 3 ? "bg-red-500 text-white" : idx < 10 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                    }`}>
                      {idx + 1}
                    </div>

                    {/* Lead info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{lead.name}</span>
                        <Badge className={`text-xs ${SCORE_COLORS[lead.score] || SCORE_COLORS.new}`}>{lead.score}</Badge>
                        <Badge variant="outline" className="text-xs capitalize">{lead.stage?.replace(/_/g, " ")}</Badge>
                        {lead.has_callback && <Badge className="bg-blue-500 text-white text-xs">Callback</Badge>}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                        <span>Source: {lead.source}</span>
                        {lead.project_interest && <span>Project: {lead.project_interest}</span>}
                        {(lead.budget_min || lead.budget_max) && <span>Budget: {formatINR(lead.budget_min)} - {formatINR(lead.budget_max)}</span>}
                      </div>
                      {lead.priority_reasons?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {lead.priority_reasons.map((r, i) => (
                            <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{r}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => fetchBrief(lead.lead_id)} data-testid={`brief-btn-${lead.lead_id}`}>
                        <FileText className="w-4 h-4 mr-1" /> Brief
                      </Button>
                      <Button size="sm" onClick={() => openOutcome(lead)} data-testid={`outcome-btn-${lead.lead_id}`}>
                        <PhoneCall className="w-4 h-4 mr-1" /> Log Call
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pre-call Brief Dialog */}
        <Dialog open={briefOpen} onOpenChange={setBriefOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="brief-dialog">
            <DialogHeader><DialogTitle>Pre-Call Brief</DialogTitle></DialogHeader>
            {briefLoading ? (
              <div className="py-8 text-center text-slate-400"><RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" /><p>Generating AI brief...</p></div>
            ) : brief ? (
              <div className="space-y-4 py-2">
                <div className="flex items-center gap-3 pb-3 border-b">
                  <div>
                    <p className="font-semibold text-slate-900">{brief.lead_name}</p>
                    <p className="text-sm text-slate-500">{brief.lead_phone} | {brief.lead_stage} | {brief.lead_score}</p>
                  </div>
                </div>

                {brief.brief?.summary && (
                  <div><div className="flex items-center justify-between mb-1"><p className="text-xs font-medium text-slate-500">Summary</p><CopyButton text={brief.brief.summary} label="brief-summary" /></div><p className="text-sm text-slate-800">{brief.brief.summary}</p></div>
                )}

                {brief.brief?.talking_points?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-slate-500">Talking Points</p>
                      <CopyButton text={brief.brief.talking_points.join("\n")} label="brief-talking-points" />
                    </div>
                    <ul className="space-y-1">
                      {brief.brief.talking_points.map((tp, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <ChevronRight className="w-3 h-3 mt-1 text-blue-500 shrink-0" />{tp}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {brief.brief?.questions_to_ask?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1">Questions to Ask</p>
                    <ul className="space-y-1">
                      {brief.brief.questions_to_ask.map((q, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <Zap className="w-3 h-3 mt-1 text-amber-500 shrink-0" />{q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {brief.brief?.potential_objections?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1">Potential Objections</p>
                    <ul className="space-y-1">
                      {brief.brief.potential_objections.map((o, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <AlertTriangle className="w-3 h-3 mt-1 text-red-400 shrink-0" />{o}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {brief.brief?.recommended_action && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-blue-700">Recommended Action</p>
                      <CopyButton text={brief.brief.recommended_action} label="brief-action" />
                    </div>
                    <p className="text-sm text-blue-800">{brief.brief.recommended_action}</p>
                  </div>
                )}

                <div className="flex gap-3 text-xs text-slate-400 pt-2 border-t">
                  <span>{brief.context?.interactions_count} interactions</span>
                  <span>{brief.context?.visits_count} visits</span>
                  <span>{brief.context?.pending_tasks} pending tasks</span>
                  {brief.brief?.tone && <Badge variant="outline" className="text-xs capitalize">{brief.brief.tone} tone</Badge>}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Call Outcome Dialog */}
        <Dialog open={outcomeOpen} onOpenChange={setOutcomeOpen}>
          <DialogContent className="sm:max-w-md" data-testid="outcome-dialog">
            <DialogHeader><DialogTitle>Log Call Outcome — {selectedLead?.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {OUTCOMES.map(o => {
                  const Icon = o.icon;
                  const selected = outcomeForm.outcome === o.value;
                  return (
                    <button key={o.value}
                      className={`p-3 rounded-lg border text-center transition-all ${selected ? "ring-2 ring-blue-400 bg-blue-50" : "hover:bg-slate-50"}`}
                      onClick={() => setOutcomeForm({ ...outcomeForm, outcome: o.value })}
                      data-testid={`outcome-${o.value}`}>
                      <Icon className={`w-5 h-5 mx-auto mb-1 ${o.color}`} />
                      <span className="text-xs font-medium text-slate-700">{o.label}</span>
                    </button>
                  );
                })}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
                <Textarea value={outcomeForm.notes} onChange={(e) => setOutcomeForm({ ...outcomeForm, notes: e.target.value })}
                  placeholder="Call notes..." rows={3} data-testid="outcome-notes" />
              </div>

              {outcomeForm.outcome === "callback" && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Callback Time</label>
                  <Input type="datetime-local" value={outcomeForm.callback_time}
                    onChange={(e) => setOutcomeForm({ ...outcomeForm, callback_time: e.target.value })} data-testid="callback-time" />
                </div>
              )}

              {outcomeForm.outcome === "connected" && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Move to Stage (optional)</label>
                  <Select value={outcomeForm.next_stage} onValueChange={(v) => setOutcomeForm({ ...outcomeForm, next_stage: v })}>
                    <SelectTrigger><SelectValue placeholder="Keep current stage" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="site_visit">Site Visit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOutcomeOpen(false)}>Cancel</Button>
              <Button onClick={submitOutcome} disabled={!outcomeForm.outcome} data-testid="submit-outcome-btn">Log Outcome</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TelecallerQueuePage;
